import React, { useState, useRef, useEffect } from 'react';
import ReactEcharts from 'echarts-for-react/esm/core';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  TooltipComponent,
  DatasetComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components';
import {
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Flex,
  VStack,
  Button,
} from '@chakra-ui/react';
import SanityImage from 'gatsby-plugin-sanity-image';
import { Table } from '../Table';
import { RichText } from '../RichText';

// Register the required components
echarts.use([
  TooltipComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
  DatasetComponent,
  LegendComponent,
  GridComponent,
]);

function seriesOptions({ data, type, colors, stacked, yAxisIndex, isHorizontal }) {
  const { rows } = data;

  const defaultColors = [
    '#599a3a',
    '#008fc4',
    '#575c5c',
    '#1d4480',
    '#d04e2b',
    '#6e3881',
    '#f1ae2a',
  ];
  const chartColors = colors ? colors.map((color) => color.hex) : defaultColors;

  const series = rows.slice(1).map((row, index) => {
    const name = row.cells[0];
    let data = row.cells.slice(1);
    // Reverse the data so it displays in the correct order
    if (isHorizontal) {
      data = data.reverse();
    }

    return {
      name,
      type,
      showSymbol: false,
      stack: stacked ? 'total' : null,
      emphasis: { focus: 'series' },
      data,
      barGap: '10',
      barCategoryGap: '10',
      color: chartColors.length > 1 ? chartColors[index] : chartColors[0],
      yAxisIndex,
    };
  });

  return series;
}

function chartOptions({ charts, isHorizontal, hideLegend }) {
  if (!charts) return;
  const series = [];
  charts.forEach(({ data, chartType, colors, stacked }, index) => {
    series.push(
      seriesOptions({
        data,
        type: chartType,
        colors,
        stacked: stacked || false,
        yAxisIndex: index,
        isHorizontal,
      }),
    );
  });

  // Labels for the axis
  let axisData = charts[0].data.rows[0].cells.slice(1);
  // Reverse the data so it displays in the correct order
  if (isHorizontal) {
    axisData = axisData.reverse();
  }

  // Get the titles of the axis
  const axisTitles = [];
  charts.forEach(({ data }) => {
    axisTitles.push(data.rows[0].cells[0]);
  });

  const axisObjects = (isHorizontal) => {
    if (isHorizontal) {
      return {
        yAxis: {
          type: 'category',
          data: axisData,
          axisLabel: {
            interval: 0,
            width: 100,
            overflow: 'break',
            fontWeight: 'bold',
            color: '#000',
            fontSize: 9,
            formatter(value) {
              return value.toString().toUpperCase();
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#dfddda',
            },
          },
        },
        xAxis: {
          type: 'value',
          splitLine: {
            show: true,
            lineStyle: {
              color: 'rgba(0, 0, 0, 0.1)', // Set the color of the grid lines
            },
          },
          axisLabel: {
            interval: 0,
            overflow: 'break',
            fontWeight: 'bold',
            color: '#000',
            fontSize: 9,
            formatter(value) {
              // For numbers of 1000 or more format to 1k
              if (value >= 1000) {
                return `${value / 1000}K`;
              }
              return value.toString().toUpperCase();
            },
          },
        },
      };
    }

    return {
      xAxis: {
        type: 'category',
        data: axisData,
        splitLine: {
          show: false,
          lineStyle: {
            color: 'rgba(0, 0, 0, 0.1)', // Set the color of the grid lines
          },
        },
        axisLabel: {
          interval: axisData.length < 5 ? 0 : 'auto',
          width: 50,
          overflow: 'break',
          fontWeight: 'bold',
          color: '#000',
          fontSize: 8,
        },
      },
      yAxis:
        series.length > 1 // Multiple series charts
          ? [
              {
                type: 'value',
                name: axisTitles[0],
                nameTextStyle: {
                  fontSize: 10,
                  fontWeight: 'bold',
                  color: '#000',
                  align: 'left',
                  verticalAlign: 'top',
                  padding: [-10, 0, 0, 0],
                },
                position: 'left',
                splitLine: {
                  show: true,
                  lineStyle: {
                    color: 'rgba(0, 0, 0, 0.1)', // Set the color of the grid lines
                  },
                },
                axisLabel: {
                  interval: 0,
                  overflow: 'break',
                  fontWeight: 'bold',
                  color: '#000',
                  fontSize: 9,
                  formatter(value) {
                    return value.toString().toUpperCase();
                  },
                },
              },
              {
                type: 'value',
                position: 'right',
                name: axisTitles[1],
                nameTextStyle: {
                  fontSize: 10,
                  fontWeight: 'bold',
                  color: '#000',
                  align: 'right',
                  verticalAlign: 'top',
                  padding: [-10, 0, 0, 0],
                },
                splitLine: {
                  show: false,
                },
                axisLabel: {
                  interval: 0,
                  overflow: 'break',
                  fontWeight: 'bold',
                  color: '#000',
                  fontSize: 9,
                  formatter(value) {
                    return value.toString().toUpperCase();
                  },
                },
              },
            ]
          : {
              // Single series charts
              type: 'value',
              splitLine: {
                show: true,
                lineStyle: {
                  color: 'rgba(0, 0, 0, 0.1)', // Set the color of the grid lines
                },
              },
              axisLabel: {
                interval: 0,
                overflow: 'break',
                fontWeight: 'bold',
                color: '#000',
                fontSize: 9,
                formatter(value) {
                  return value.toString().toUpperCase();
                },
              },
            },
    };
  };

  const chartOptionObject = {
    xAxis: axisObjects(isHorizontal).xAxis,
    yAxis: axisObjects(isHorizontal).yAxis,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    grid: {
      containLabel: true,
      top: axisTitles.length > 1 ? '30' : '10',
      left: axisTitles.length > 1 ? '10' : '0',
      right: axisTitles.length > 1 ? '10' : '0',
      // eslint-disable-next-line no-nested-ternary
      height: hideLegend ? '85%' : axisTitles.length > 1 ? '75%' : '80%',
    },
    legend: hideLegend
      ? null
      : {
          orient: 'horizontal',
          type: 'scroll',
          left: 0,
          bottom: 0,
          textStyle: {
            fontWeight: 'bold',
            ellipsis: '...',
            overflow: 'truncate',
            width: 195,
          },
          tooltip: {
            show: true,
            showDelay: 500,
            triggerOn: 'mousemove',
            confine: true,
          },
          itemWidth: 15,
        },
    series: series.flat(),
  };

  return chartOptionObject;
}

export default function Chart({ data }) {
  const {
    figureNumber,
    title,
    subTitle,
    source,
    image,
    description,
    series,
    isHorizontal,
    hideLegend,
  } = data || {};
  const [showTable, setShowTable] = useState(false);
  const chartRef = useRef(null);

  // Resize the chart on browser resize
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        const chartInstance = chartRef.current.getEchartsInstance();
        chartInstance.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const ChartHeader = () => (
    <VStack mb={20} alignItems='flex-start' spacing='2px'>
      {figureNumber && (
        <Box fontSize='xs' textTransform='uppercase' fontWeight={500} letterSpacing={1}>
          <Text>Figure {figureNumber}</Text>
        </Box>
      )}
      {title && <RichText value={title} textStyle='h4' fontWeight={600} />}
      {subTitle && <RichText value={subTitle} fontSize='14px' fontWeight={500} lineHeight={1.1} />}
    </VStack>
  );

  const ChartFooter = () => (
    <Flex
      justifyContent='space-between'
      mt={10}
      flexDirection='column'
      alignItems='flex-start'
      gap={10}
    >
      {description && (
        <RichText value={description} fontWeight={600} fontSize='1rem' lineHeight={1.3} />
      )}
      {source && (
        <RichText flex='1' value={source} fontSize='xs' fontWeight={300} mt={5} lineHeight={1.3} />
      )}
    </Flex>
  );

  return (
    <Box className='chart'>
      <Box background='grey.light' padding={[20, 30]}>
        <ChartHeader />
        {image && !series?.length && <SanityImage {...image} alt={image?.asset?.altText} />}
        {!image && series?.length && (
          <Box
            as={ReactEcharts}
            maxHeight='250px'
            echarts={echarts}
            option={chartOptions({
              charts: series,
              isHorizontal,
              hideLegend,
            })}
            ref={chartRef}
          />
        )}
        <Modal isOpen={showTable} onClose={() => setShowTable(false)} size='4xl'>
          <ModalOverlay
            background='rgba(255, 255, 255, 0.5)'
            backdropFilter='auto'
            backdropBlur='6px'
          />
          <ModalContent>
            <ModalCloseButton background='white' padding={20} borderRadius='100%' />
            <ModalBody padding={30} width='100%'>
              <ChartHeader />
              {image ? (
                <SanityImage {...image} alt={image?.asset?.altText} width={850} />
              ) : (
                <Table series={series} />
              )}
              <ChartFooter />
            </ModalBody>
          </ModalContent>
        </Modal>
        <Button onClick={() => setShowTable(!showTable)} variant='secondary' mt={20} width='100%'>
          <Flex gap={10} alignItems='center'>
            {image ? 'View Larger' : 'View As Table'}
          </Flex>
        </Button>
      </Box>
      <ChartFooter />
    </Box>
  );
}
